import React from 'react';
import {
    Autocomplete,
    CircularProgress,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {subscribeToWhiteboards} from '../../../firebase/firestore/whiteboards';
import UserAvatar from '../../../components/UserAvatar';

const EMPTY_EXCLUDE_IDS = [];

export default function ChartLinkPicker({
    active = false,
    whiteboardId,
    excludeIds = EMPTY_EXCLUDE_IDS,
    onSelect,
    onCancel,
    saving = false,
}) {
    const [charts, setCharts] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [loadError, setLoadError] = React.useState('');

    const excluded = React.useMemo(() => new Set(excludeIds), [excludeIds]);

    React.useEffect(() => {
        if (!active) {
            setLoadError('');
            return undefined;
        }

        setLoading(true);
        setLoadError('');
        const unsubscribe = subscribeToWhiteboards(
            (rows) => {
                const next = (Array.isArray(rows) ? rows : [])
                    .filter((row) => row?.id && row.id !== whiteboardId && !excluded.has(row.id))
                    .map((row) => ({
                        id: row.id,
                        title: row.title || row.id,
                        ownerId: row.ownerId || row.owner_id || row.userId || null,
                        ownerDisplayName: row.userDisplayName || null,
                    }))
                    .sort((a, b) => String(a.title).localeCompare(String(b.title)));
                setCharts(next);
                setLoading(false);
            },
            (err) => {
                setLoadError(err?.message || 'Failed to load charts.');
                setLoading(false);
            },
        );

        return () => {
            try {
                unsubscribe?.();
            } catch {
                // cleanup
            }
        };
    }, [active, whiteboardId, excluded]);

    const filteredCharts = React.useCallback((options, query) => {
        const trimmed = String(query || '').trim().toLowerCase();
        if (!trimmed) return options;
        return options.filter((chart) => {
            const title = String(chart.title || '').toLowerCase();
            const id = String(chart.id || '').toLowerCase();
            return title.includes(trimmed) || id.includes(trimmed);
        });
    }, []);

    const handleChange = (_event, value) => {
        if (saving || !value) return;
        onSelect?.(value);
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Escape' && !saving) {
            event.preventDefault();
            event.stopPropagation();
            onCancel?.();
        }
    };

    if (!active) return null;

    return (
        <Autocomplete
            openOnFocus
            autoHighlight
            disablePortal
            size="small"
            fullWidth
            options={charts}
            getOptionLabel={(option) => option.title || option.id || ''}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            loading={loading}
            disabled={saving}
            filterOptions={(opts, {inputValue}) => filteredCharts(opts, inputValue)}
            onChange={handleChange}
            noOptionsText={loadError || 'No charts found.'}
            renderInput={(params) => (
                <TextField
                    {...params}
                    autoFocus
                    label="Chart"
                    placeholder="Search charts…"
                    error={Boolean(loadError)}
                    helperText={loadError || undefined}
                    onKeyDown={handleKeyDown}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading || saving ? (
                                    <CircularProgress color="inherit" size={14}/>
                                ) : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
            renderOption={(props, option) => {
                const {key, ...liProps} = props;
                const ownerUser = option.ownerId
                    ? {userId: option.ownerId, displayName: option.ownerDisplayName, hasAvatar: true}
                    : null;
                return (
                    <li key={key} {...liProps} style={{...liProps.style, paddingTop: 6, paddingBottom: 6}}>
                        <Stack spacing={0.25} sx={{width: '100%', minWidth: 0}}>
                            <Typography variant="body2" sx={{whiteSpace: 'normal', wordBreak: 'break-word'}}>
                                {option.title}
                            </Typography>
                            {ownerUser && (
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                    <UserAvatar
                                        user={ownerUser}
                                        userId={option.ownerId}
                                        sx={{width: 14, height: 14, fontSize: 8}}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        {option.ownerDisplayName || option.ownerId}
                                    </Typography>
                                </Stack>
                            )}
                        </Stack>
                    </li>
                );
            }}
            slotProps={{
                popper: {
                    placement: 'bottom-start',
                    sx: {zIndex: (theme) => theme.zIndex.modal + 1},
                },
            }}
        />
    );
}
